/**
 * Normalises a raw RTDB test node written by the hardware into a clean,
 * time-aligned series. Works with any number of samples.
 *
 * Device format (per sample): accelX/Y/Z (raw ADC counts), gyroX/Y/Z (deg/s),
 * magX/Y/Z (uT), timestamp (unix seconds, ~2 samples share one second).
 * Test node: start, stop (unix seconds), durationMs, sampleCount, rfid, status,
 * candidateInfo, optional storeAsReference flag.
 */

const ENV = {};

// Analog accelerometer: ~410 ADC counts per 1g (Z rest ~ +420 counts over X/Y).
const ACCEL_COUNTS_PER_G = Number(ENV.ACCEL_COUNTS_PER_G || 410);
const G = 9.80665;

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
const round = (v, d = 3) => (v === null || !Number.isFinite(v) ? null : Math.round(v * 10 ** d) / 10 ** d);
const median = (xs) => {
  const s = xs.filter((x) => x !== null).sort((a, b) => a - b);
  if (!s.length) return 0;
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/** Convert seconds or milliseconds epoch to milliseconds. */
function toMs(v) {
  const n = num(v);
  if (n === null) return null;
  return n > 1e11 ? n : n * 1000;
}

const REFERENCE_KEYS = ["storeasreference", "storeasreferance", "isreference", "reference", "referencedata"];

/** True if the test node carries a reference flag (any spelling / truthy value). */
function isReferenceTest(test) {
  if (!test || typeof test !== "object") return false;
  return Object.entries(test).some(([k, v]) => {
    if (!REFERENCE_KEYS.includes(k.toLowerCase())) return false;
    return v === true || v === 1 || ["true", "yes", "1"].includes(String(v).toLowerCase());
  });
}

/** Candidate details with both testCentre / testCenter spellings supported. */
function normalizeCandidate(test = {}) {
  const c = test.candidateInfo || test.candidate || test.userInfo || test.user || {};
  return {
    name: c.name || test.candidateName || test.name || null,
    age: c.age ?? test.age ?? null,
    licenseType: c.licenseType || c.licenceType || test.licenseType || null,
    vehicleType: c.vehicleType || c.vehicle || test.vehicleType || null,
    testCenter: c.testCentre || c.testCenter || test.testCentre || test.testCenter || null,
    licenseNumber: c.licenseNumber || c.licenceNumber || null,
    phone: c.phone || c.mobile || null,
  };
}

function rawPoints(test) {
  const raw = test?.sensorData;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.keys(raw).sort().map((k) => raw[k]);
  return list.filter((p) => p && typeof p === "object");
}

/**
 * Returns { session, series } where series points carry:
 * t (s since start), ts (device timestamp ms), ax/ay/az (g, deviation from rest),
 * aMag (g), gx/gy/gz (deg/s), mx/my/mz, heading (deg, unwrapped).
 */
function normalizeTest(test) {
  const all = rawPoints(test).map((p, idx) => ({ ...p, _idx: idx }));
  // Stable sort: keep device order for equal timestamps
  all.sort((a, b) => (toMs(a.timestamp) ?? 0) - (toMs(b.timestamp) ?? 0) || a._idx - b._idx);

  const startMs = toMs(test?.start) ?? toMs(all[0]?.timestamp);
  const stopMs = toMs(test?.stop) ?? toMs(all[all.length - 1]?.timestamp);

  // Keep only samples recorded between start and stop (fall back to all if window is empty/invalid)
  let windowed = all;
  if (startMs !== null && stopMs !== null && stopMs >= startMs) {
    const w = all.filter((p) => {
      const ts = toMs(p.timestamp);
      return ts === null || (ts >= startMs && ts <= stopMs);
    });
    if (w.length >= 3) windowed = w;
  }

  const n = windowed.length;
  let durationMs = num(test?.durationMs);
  if (!durationMs && num(test?.durationSeconds)) durationMs = num(test.durationSeconds) * 1000;
  if (!durationMs && startMs !== null && stopMs !== null) durationMs = stopMs - startMs;
  if (!durationMs && n > 1) durationMs = (toMs(windowed[n - 1].timestamp) ?? 0) - (toMs(windowed[0].timestamp) ?? 0);
  durationMs = Math.max(0, durationMs || 0);

  // Device timestamps only have 1s resolution -> spread samples evenly across the recorded duration
  const dtSec = n > 1 && durationMs > 0 ? durationMs / 1000 / (n - 1) : 0.5;

  // Accel units: raw ADC counts (large values) or m/s^2 (small values)
  const sampleAccel = windowed.map((p) => Math.abs(num(p.accelX) ?? 0));
  const rawCounts = median(sampleAccel) > 50;
  const toG = (v) => (rawCounts ? v / ACCEL_COUNTS_PER_G : v / G);
  const base = {
    x: median(windowed.map((p) => num(p.accelX))),
    y: median(windowed.map((p) => num(p.accelY))),
    z: median(windowed.map((p) => num(p.accelZ))),
  };

  const hasGyro = windowed.some((p) => num(p.gyroZ) !== null);
  let prevHeading = null;
  let unwrap = 0;

  const series = windowed.map((p, i) => {
    const ax = toG((num(p.accelX) ?? base.x) - base.x);
    const ay = toG((num(p.accelY) ?? base.y) - base.y);
    const az = toG((num(p.accelZ) ?? base.z) - base.z);
    const mx = num(p.magX);
    const my = num(p.magY);
    let heading = null;
    if (mx !== null && my !== null) {
      let h = (Math.atan2(my, mx) * 180) / Math.PI;
      if (prevHeading !== null) {
        const diff = h + unwrap - prevHeading;
        if (diff > 180) unwrap -= 360;
        else if (diff < -180) unwrap += 360;
      }
      h += unwrap;
      prevHeading = h;
      heading = h;
    }
    return {
      i,
      t: round(i * dtSec, 3),
      ts: toMs(p.timestamp),
      ax: round(ax, 4),
      ay: round(ay, 4),
      az: round(az, 4),
      aMag: round(Math.sqrt(ax * ax + ay * ay + az * az), 4),
      gx: round(num(p.gyroX) ?? 0, 3),
      gy: round(num(p.gyroY) ?? 0, 3),
      gz: round(num(p.gyroZ) ?? 0, 3),
      mx: round(mx, 3),
      my: round(my, 3),
      mz: round(num(p.magZ), 3),
      heading: round(heading, 2),
    };
  });

  return {
    session: {
      start: test?.start ?? null,
      stop: test?.stop ?? null,
      startMs,
      stopMs,
      durationMs: Math.round(durationMs),
      sampleCountReported: num(test?.sampleCount),
      sampleCountRaw: all.length,
      sampleCountUsed: n,
      sampleRateHz: dtSec > 0 ? round(1 / dtSec, 2) : null,
      dtSec: round(dtSec, 4),
      accelUnits: rawCounts ? "adc_counts" : "m/s2",
      accelCountsPerG: rawCounts ? ACCEL_COUNTS_PER_G : null,
      accelBaseline: { x: round(base.x, 2), y: round(base.y, 2), z: round(base.z, 2) },
      hasGyro,
    },
    series,
  };
}

export { normalizeTest, normalizeCandidate, isReferenceTest, toMs, median, round, ACCEL_COUNTS_PER_G };
